<template>
  <div
    class="AssetLiabilitiesBarChart"
    :class="{
      'AssetLiabilitiesBarChart--hasLiabilities': liabilitiesAmount
    }"
    :data-scale-type="scaleType"
  >
    <div class="AssetLiabilitiesBarChart__chart">
      <svg
        class="AssetLiabilitiesBarChart__svg"
      >
        <defs>
          <pattern
            :id="patternId"
            patternUnits="userSpaceOnUse"
            stroke="var(--colorSwissBlack)"
            width="4"
            height="4"
            patternTransform="rotate(115)"
          >
            <path d="M0,2.5 L4,2.5" />
          </pattern>

          <mask
            v-if="targetRange && scale"
            :id="maskId"
          >
            <rect
              fill="white"
              width="100%"
              :style="{
                height: targetRangeHeight,
                // @ts-expect-error: y is not recognized in style objects on svg tags
                y: targetRangeY
              }"
            />

            <rect
              fill="black"
              :style="{
                height: '22px',
                width: `${targetRangeLabelWidth}px`,
                // @ts-expect-error: y is not recognized in style objects on svg tags
                y: targetRangeY
              }"
            />
          </mask>
        </defs>

        <rect
          fill="var(--colorSwissTeal300)"
          width="100%"
          data-testid="assets"
          :style="{
            height: `calc(100% * ${assetRatio})`,
            // @ts-expect-error: y is not recognized in style objects on svg tags
            y: `calc(100% - calc(100% * ${assetRatio + liabilitiesRatio}))`
          }"
        />

        <rect
          v-if="liabilitiesAmount"
          fill="var(--colorSwissPurple300)"
          width="100%"
          data-testid="liabilities"
          :style="{
            height: `calc(100% * ${liabilitiesRatio} - 1px)`,
            // @ts-expect-error: y is not recognized in style objects on svg tags
            y: `calc(100% - calc(100% * ${liabilitiesRatio}) + 1px)`,
          }"
        />

        <rect
          v-if="targetRange"
          width="100%"
          data-testid="targetRange"
          :fill="`url(#${patternId})`"
          :style="{
            height: targetRangeHeight,
            // @ts-expect-error: y is not recognized in style objects on svg tags
            y: targetRangeY,
          }"
          :mask="`url(#${maskId})`"
        />
      </svg>

      <div
        v-if="scale"
        class="AssetLiabilitiesBarChart__axis"
      >
        <div
          v-for="(point, index) in scale"
          :key="point.label || point.value + index"
          class="AssetLiabilitiesBarChart__axisTick"
          :class="{
            'AssetLiabilitiesBarChart__axisTick--isAbsolute': point.y !== undefined,
            'AssetLiabilitiesBarChart__axisTick--isHighlighted': point.isHighlighted,
            'AssetLiabilitiesBarChart__axisTick--isOverlapping': Math.abs((point?.y ?? 0) - (scale[index + 1]?.y ?? 1)) < 0.03,
          }"
          :style="`--y: ${point.y}`"
          :data-testid="(point?.y ?? 0) - (scale[index + 1]?.y ?? 0)"
        >
          <UIText
            v-if="point.label"
            class="AssetLiabilitiesBarChart__axisTickLabel"
            size="2XSmall"
            weight="Medium"
          >
            {{ point.label }}
          </UIText>
        </div>
      </div>

      <div
        v-if="valueLabel"
        class="AssetLiabilitiesBarChart__valueLabel"
        :class="{
          'AssetLiabilitiesBarChart__valueLabel--isFlipped': isValueLabelFlipped,
        }"
      >
        <UIText
          v-if="valueLabelPrefix"
          weight="Medium"
          size="Small"
        >
          {{ valueLabelPrefix }}
        </UIText>

        <UIText
          weight="Medium"
          size="XLarge"
        >
          {{ valueLabel }}
        </UIText>
      </div>

      <div
        v-if="targetRange && scale"
        ref="targetRangeLabelRef"
        class="AssetLiabilitiesBarChart__targetRangeLabel"
      >
        <UIText
          size="2XSmall"
          weight="Medium"
        >
          {{ targetRangeLabel }}
        </UIText>
      </div>
    </div>

    <div
      v-if="includeLegend"
      class="AssetLiabilitiesBarChart__legend"
    >
      <div
        v-for="item in legendItems"
        :key="item.label"
        class="AssetLiabilitiesBarChart__legendItem"
      >
        <div
          class="AssetLiabilitiesBarChart__legendSwatch"
          :style="{background: item.color}"
        />

        <UIText weight="Medium">
          {{ item.label }}
        </UIText>

        <UIText
          weight="Medium"
          size="XLarge"
        >
          {{ formatAmount(item.amount, {excludeCents: true}) }}
        </UIText>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import UIText from "@cosine/components/UIText.vue";
import formatAmount from "@cosine/lib/utils/financial/formatAmount";
import { computed, onMounted, ref, useId } from "vue";

const {
  targetRange,
  targetRangeLabel = "Target range",
  valueLabel,
  valueLabelPrefix,
  scale,
  assetAmount,
  liabilitiesAmount,
  targetRangeIntervalValue,
  includeLegend = false,
  scaleType = "linear",
} = defineProps<{
  assetAmount: number,
  liabilitiesAmount?: number,
  targetRange?: [number, number],
  targetRangeLabel?: string,
  valueLabel?: string,
  valueLabelPrefix?: string,
  targetRangeIntervalValue?: number,
  includeLegend?: boolean,
  scale?: Array<{
    label?: string,
    value: number,
    y?: number,
    isHighlighted?: boolean,
  }>,
  scaleType?: "linear" | "asymmetric",
}>();

const targetRangeLabelRef = ref<SVGSVGElement>();
const targetRangeLabelWidth = ref<number>(0);

const patternId = `pattern-${useId()}`;
const maskId = `mask-${useId()}`;

const totalAmount = computed(() => {
  const targetRangeEnd = targetRange?.[1] ?? 0;
  const targetRangeMaxValue = targetRangeEnd * (targetRangeIntervalValue ?? 0);
  return Math.max(assetAmount + (liabilitiesAmount ?? 0), targetRangeMaxValue);
});

const assetRatio = computed(() => {
  return assetAmount / totalAmount.value;
});

const liabilitiesRatio = computed(() => {
  return (liabilitiesAmount ?? 0) / totalAmount.value;
});

const isValueLabelFlipped = computed(() => {
  return liabilitiesRatio.value < 0.16;
});

const scaleLength = computed(() => {
  if (!scale) return 1;
  return scale.length;
});

const targetRangeEnd = computed(() => {
  if (!targetRange) return null;
  return targetRange[1];
});

const targetRangeDelta = computed(() => {
  if (!targetRange) return null;

  return (targetRange[1] - targetRange[0]);
});

const targetRangeHeight = computed(() => {
  if (!targetRange || !scale) return 0;
  const denominator = scaleType === "linear" ? scale.length - 1 : 1;
  return `calc(100% * (${targetRangeDelta.value}) / ${denominator})`;
});

const targetRangeY = computed(() => {
  if (!targetRange || !scale) return 0;
  const denominator = scaleType === "linear" ? scale.length - 1 : 1;
  return `calc(100% - (100% * ${targetRangeEnd.value} / ${denominator}))`;
});

const legendItems = computed(() => {
  return [
    {
      label: "Assets",
      amount: assetAmount,
      color: "var(--colorSwissTeal300)",
    },
    ...(liabilitiesAmount
      ? [
        {
          label: "Liabilities",
          amount: liabilitiesAmount,
          color: "var(--colorSwissPurple300)",
        },
      ]
      : []),
  ];

});

onMounted(async () => {
  if (!targetRangeLabelRef.value) return;
  targetRangeLabelWidth.value = targetRangeLabelRef.value.getBoundingClientRect().width + 12;
});
</script>

<style scoped lang="scss">
.AssetLiabilitiesBarChart {
  isolation: isolate;

  display: flex;
  flex-direction: column;
  width: 100%;
}

.AssetLiabilitiesBarChart__chart {
  position: relative;
  display: flex;
  width: 100%;
}

.AssetLiabilitiesBarChart__svg {
  display: block;
  width: 100%;
  min-height: 350px;
}

.AssetLiabilitiesBarChart__axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-block: 1px;

  &:has(.AssetLiabilitiesBarChart__axisTick--isAbsolute) {
    min-width: 48px;
  }
}

.AssetLiabilitiesBarChart__axisTick {
  --tickWidth: 6px;
  z-index: 2;

  display: flex;
  gap: 6px;
  align-items: center;
  height: 0px;

  &::before {
    display: block;
    width: var(--tickWidth);
    height: 1px;

    background-color: var(--colorSwissBlack);

    content: "";
  }
}

.AssetLiabilitiesBarChart__axisTick--isOverlapping {
  display: none;
}

.AssetLiabilitiesBarChart__axisTick--isAbsolute {
  position: absolute;
  top: calc(100% * (1 - var(--y)));
}

.AssetLiabilitiesBarChart__axisTick--isHighlighted {
  gap: 0px;

  &::before {
    background-color: transparent;
  }

  .AssetLiabilitiesBarChart--hasLiabilities & {
    transform: translateY(-0.5px);
  }

  & .AssetLiabilitiesBarChart__axisTickLabel {
    --triangleSize: 8px;
    margin-left: calc(-1 * var(--triangleSize) - var(--tickWidth));
    padding: 2px 4px;
    padding-left: calc(var(--triangleSize) + 4px);

    background-color: var(--colorSwissBlack);
    color: var(--colorWhite);
    clip-path: polygon(var(--triangleSize) 0%, 100% 0%, 100% 100%, var(--triangleSize) 100%, 0% 50%);
  }
}

.AssetLiabilitiesBarChart__valueLabel {
  position: absolute;
  bottom: 0;
  z-index: 1;

  display: flex;
  flex-direction: column;
  width: calc(100% - 48px);

  span {
    width: fit-content;
    padding: 2px 4px;

    background-color: var(--colorSwissBlack);
    color: var(--colorWhite);
  }

  .AssetLiabilitiesBarChart--hasLiabilities & {
    bottom: calc(100% - 100% * v-bind(liabilitiesRatio));
    border-bottom: 1px solid var(--colorSwissBlack);
  }

  .AssetLiabilitiesBarChart--hasLiabilities &.AssetLiabilitiesBarChart__valueLabel--isFlipped {
    top: 0;
    border: none;
  }
}

.AssetLiabilitiesBarChart__targetRangeLabel {
  --ratio: calc(v-bind(targetRangeEnd) / (v-bind(scaleLength) - 1));
  position: absolute;
  top: calc(100% - (100% * var(--ratio)));
  left: 4px;

  [data-scale-type="asymmetric"] & {
    --ratio: v-bind(targetRangeEnd);
  }
}

.AssetLiabilitiesBarChart__legend {
  margin-top: 20px;
}

.AssetLiabilitiesBarChart__legendItem {
  display: grid;
  grid-template-columns: 14px 1fr auto;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  & + & {
    border-top: 1px solid var(--themeColorBorder);
  }
}

.AssetLiabilitiesBarChart__legendSwatch {
  width: 14px;
  height: 14px;

  border-radius: 9999px;
  background-color: var(--colorSwissBlack);
}
</style>
